import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Company } from 'src/app/models/data/company';
import { Device } from 'src/app/models/data/device';
import { Location } from 'src/app/models/data/location';
import { User } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import {
	BehaviorSubject,
	Observable,
	of,
	interval,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export type KeyDetectionReportFilterOptions =
	Components.Schemas.KeyDetectionReportFilterOptions;

export type KeyDetectionReportFilters = {
	deviceIds?: { id: string; serialNumber: string }[];
	deviceOwnerCompanyIds?: { id: string; name: string }[];
	accessedByCompanyIds?: { id: string; name: string }[];
	accessedByUserIds?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[];
	propertyIds?: { id: string; name: string }[];
	firstName?: Client[];
	lastName?: Client[];
	clientIdn?: Client[];
	visitStartDate?: Date;
	visitEndDate?: Date;
};

export const emptyKeyDetectionReportFilters =
	(): KeyDetectionReportFilters => ({
		deviceIds: [],
		deviceOwnerCompanyIds: [],
		accessedByCompanyIds: [],
		accessedByUserIds: [],
		propertyIds: [],
		firstName: [],
		lastName: [],
		clientIdn: [],
		visitStartDate: undefined,
		visitEndDate: undefined,
	});

interface SearchConfig {
	type:
		| 'device'
		| 'deviceOwner'
		| 'accessedByUser'
		| 'property'
		| 'firstName'
		| 'lastName'
		| 'clientIdn';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

@Component({
	selector: 'app-key-detection-report-dialog',
	templateUrl: './key-detection-report-dialog.component.html',
	styleUrls: [
		'./key-detection-report-dialog.component.scss',
		'../../common/main.scss',
	],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatDialogActions,
		MatButton,
	],
})
export class KeyDetectionReportDialogComponent
	implements AfterViewInit, OnDestroy
{
	keyDetectionReportFilters: KeyDetectionReportFilters =
		emptyKeyDetectionReportFilters();

	filterOptions?: KeyDetectionReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	deviceOptions?: { id: string; serialNumber: string }[] = [];
	deviceOwnerOptions?: { id: string; name: string }[] = [];
	accessedByCompanyOptions?: { id: string; name: string }[] = [];
	accessedByUserOptions?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		displayName: string;
	}[] = [];
	propertyOptions?: { id: string; name: string }[] = [];
	firstNames?: Client[] = [];
	lastNames?: Client[] = [];
	clientIdns?: Client[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	queryParams: QueryParam[] = [];

	deviceIds: string[] = [];
	deviceOwnersCompanyIds: string[] = [];
	accessedByCompanyIds: string[] = [];
	accessedByUserIds: string[] = [];
	propertyIds: string[] = [];
	firstName: string[] = [];
	lastName: string[] = [];
	clientIdn: string[] = [];

	deviceMultiSelectorSearchPlaceHolder = $localize`:@@keyDetectionReportDialog-deviceMultiSelectorSearchPlaceHolder:Search Serial Numbers (optional)`;
	deviceOwnerMultiSelectorSearchPlaceHolder = $localize`:@@keyDetectionReportDialog-deviceOwnerMultiSelectorSearchPlaceHolder:Search Companies (optional)`;
	accessedByCompanyMultiSelectorSearchPlaceHolder = $localize`:@@keyDetectionReportDialog-accessedByCompanyMultiSelectorSearchPlaceHolder:Search Companies (optional)`;
	accessedByUserMultiSelectorSearchPlaceHolder = $localize`:@@keyDetectionReportDialog-accessedByUserMultiSelectorSearchPlaceHolder:Search Users (optional)`;
	propertyMultiSelectorSearchPlaceHolder = $localize`:@@keyDetectionReportDialog-propertyMultiSelectorSearchPlaceHolder:Search Properties (optional)`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOptions || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				id: device.device.id,
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.keyDetectionReportFilters.deviceIds = [...items];
			},
		},
		deviceOwner: {
			type: 'deviceOwner',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOwnerOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.keyDetectionReportFilters.deviceOwnerCompanyIds = [...items];
			},
		},
		accessedByUser: {
			type: 'accessedByUser',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.accessedByUserOptions || [],
			apiCall: (params) =>
				this.apiService.getCompanyUsersByCompanyId(
					this.activeCompanyId,
					params
				),
			mapResponse: (user) => ({
				id: user.user.id,
				firstName: user.user.firstName,
				lastName: user.user.lastName,
				email: user.user.email,
				displayName: `${user.user.firstName} ${user.user.lastName} (${user.user.email})`,
			}),
			updateFilters: (items) => {
				this.keyDetectionReportFilters.accessedByUserIds = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.propertyOptions || [],
			apiCall: (params) => this.apiService.getProperties(params),
			mapResponse: (property) => ({
				id: property.property.id,
				name: property.property.name,
			}),
			updateFilters: (items) => {
				this.keyDetectionReportFilters.propertyIds = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.keyDetectionReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.keyDetectionReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdns || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.keyDetectionReportFilters.clientIdn = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<KeyDetectionReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		private readonly router: Router,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: KeyDetectionReportFilters;
			filterOptions: KeyDetectionReportFilterOptions;
		}
	) {
		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;
		if (this.data?.reportFilters) {
			this.keyDetectionReportFilters = this.data.reportFilters;
		}

		// Initialize deviceOptions with previous selections
		if (this.keyDetectionReportFilters.deviceIds?.length) {
			this.keyDetectionReportFilters.deviceIds.forEach((device) => {
				const deviceOption = {
					id: device.id,
					serialNumber: device.serialNumber,
				};
				this.deviceIds.push(device.id);
				this.searchConfigs.device.selectedItems.add(deviceOption);
			});
			this.deviceOptions = Array.from(this.searchConfigs.device.selectedItems);
		}

		// Initialize deviceOwnerOptions
		if (this.keyDetectionReportFilters.deviceOwnerCompanyIds?.length) {
			this.keyDetectionReportFilters.deviceOwnerCompanyIds.forEach(
				(company) => {
					const companyOption = {
						id: company.id,
						name: company.name,
					};
					this.deviceOwnersCompanyIds.push(company.id);
					this.searchConfigs.deviceOwner.selectedItems.add(companyOption);
				}
			);
			this.deviceOwnerOptions = Array.from(
				this.searchConfigs.deviceOwner.selectedItems
			);
		}

		// Initialize accessedByUserOptions with previous selections
		if (this.keyDetectionReportFilters.accessedByUserIds?.length) {
			this.keyDetectionReportFilters.accessedByUserIds.forEach((user) => {
				const userOption = {
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					displayName: `${user.firstName} ${user.lastName} (${user.email})`,
				};
				this.accessedByUserIds.push(user.id);
				this.searchConfigs.accessedByUser.selectedItems.add(userOption);
			});
			// Initialize accessedByUserOptions with selected users
			this.accessedByUserOptions = Array.from(
				this.searchConfigs.accessedByUser.selectedItems
			);
		}

		// Initialize propertyOptions with previous selections
		if (this.keyDetectionReportFilters.propertyIds?.length) {
			this.keyDetectionReportFilters.propertyIds.forEach((property) => {
				const propertyOption = {
					id: property.id,
					name: property.name,
				};
				this.propertyIds.push(property.id);
				this.searchConfigs.property.selectedItems.add(propertyOption);
			});
			// Initialize propertyOptions with selected properties
			this.propertyOptions = Array.from(
				this.searchConfigs.property.selectedItems
			);
		}

		this.checkCareCompany()
			.then(() => {})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNames || []);
					case 'lastName':
						return of(this.lastNames || []);
					case 'clientIdn':
						return of(this.clientIdns || []);
				}
			}
		} else {
			if (!searchTerm || searchTerm === '' || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.deviceOptions || []);
					case 'deviceOwner':
						return of(this.deviceOwnerOptions || []);
					case 'accessedByUser':
						return of(this.accessedByUserOptions || []);
					case 'property':
						return of(this.propertyOptions || []);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				switch (config.type) {
					case 'device':
						this.deviceOptions = mergedOptions;
						break;
					case 'deviceOwner':
						this.deviceOwnerOptions = mergedOptions;
						break;
					case 'accessedByUser':
						this.accessedByUserOptions = mergedOptions;
						break;
					case 'property':
						this.propertyOptions = mergedOptions;
						break;
					case 'firstName':
						this.firstNames = mergedOptions;
						break;
					case 'lastName':
						this.lastNames = mergedOptions;
						break;
					case 'clientIdn':
						this.clientIdns = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}

	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'deviceOwner':
				return 'companyName';
			case 'accessedByUser':
				return 'name';
			case 'property':
				return 'propertyName';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			default:
				return '';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;
	}

	get hasFilters(): boolean {
		return (
			JSON.stringify(this.keyDetectionReportFilters) !==
			JSON.stringify(emptyKeyDetectionReportFilters())
		);
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@keyDetectionReportDialog-generate:Generate`
			: $localize`:@@keyDetectionReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedDeviceChangedEvent(device: any): void {
		if (device === undefined) {
			return;
		}
		this.keyDetectionReportFilters.deviceIds = [...device];
	}

	onSelectedCompaniesChangedEvent(deviceOwner: any): void {
		if (deviceOwner === undefined) {
			return;
		}
		this.keyDetectionReportFilters.deviceOwnerCompanyIds = [...deviceOwner];
	}

	onSelectedPropertyChangedEvent(property: any): void {
		if (property === undefined) {
			return;
		}
		this.keyDetectionReportFilters.propertyIds = [...property];
	}

	onSelectedUserChangedEvent(accessedByUser: any): void {
		if (accessedByUser === undefined) {
			return;
		}
		this.keyDetectionReportFilters.accessedByUserIds = [...accessedByUser];
	}

	onStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.keyDetectionReportFilters.visitStartDate = event.value ?? undefined;
	}

	onEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.keyDetectionReportFilters.visitEndDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();
		this.router
			.navigateByUrl(`/reports/keyDetectionReport`, {
				state: {
					reportFilters: this.keyDetectionReportFilters,
					compiledFilters,
					hasFilters: this.hasFilters,
					filterOptions: this.filterOptions,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.keyDetectionReportFilters.deviceIds &&
			this.keyDetectionReportFilters.deviceIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceIds',
				value: this.keyDetectionReportFilters.deviceIds.map((it) => it.id),
			});
		}
		if (
			this.keyDetectionReportFilters.deviceOwnerCompanyIds &&
			this.keyDetectionReportFilters.deviceOwnerCompanyIds.length > 0
		) {
			queryParameters.push({
				param: 'deviceOwnersCompanyIds',
				value: this.keyDetectionReportFilters.deviceOwnerCompanyIds.map(
					(it) => it.id
				),
			});
		}
		if (
			this.keyDetectionReportFilters.accessedByCompanyIds &&
			this.keyDetectionReportFilters.accessedByCompanyIds.length > 0
		) {
			queryParameters.push({
				param: 'accessedByCompanyIds',
				value: this.keyDetectionReportFilters.accessedByCompanyIds.map(
					(it) => it.id
				),
			});
		}
		if (
			this.keyDetectionReportFilters.accessedByUserIds &&
			this.keyDetectionReportFilters.accessedByUserIds.length > 0
		) {
			queryParameters.push({
				param: 'accessedByUserIds',
				value: this.keyDetectionReportFilters.accessedByUserIds.map(
					(it) => it.id
				),
			});
		}
		if (
			this.keyDetectionReportFilters.propertyIds &&
			this.keyDetectionReportFilters.propertyIds.length > 0
		) {
			queryParameters.push({
				param: 'propertyIds',
				value: this.keyDetectionReportFilters.propertyIds.map((it) => it.id),
			});
		}
		if (this.keyDetectionReportFilters.visitStartDate) {
			queryParameters.push({
				param: 'visitStartDate',
				value: this.keyDetectionReportFilters.visitStartDate
					.toISOString()
					.split('T')[0],
			});
		}
		if (this.keyDetectionReportFilters.visitEndDate) {
			queryParameters.push({
				param: 'visitEndDate',
				value: this.keyDetectionReportFilters.visitEndDate
					.toISOString()
					.split('T')[0],
			});
		}
		if (
			this.keyDetectionReportFilters.firstName &&
			this.keyDetectionReportFilters.firstName.length > 0
		) {
			queryParameters.push({
				param: 'firstNames',
				value: this.keyDetectionReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}
		if (
			this.keyDetectionReportFilters.lastName &&
			this.keyDetectionReportFilters.lastName.length > 0
		) {
			queryParameters.push({
				param: 'lastNames',
				value: this.keyDetectionReportFilters.lastName.map(
					(it) => it.lastName || ''
				),
			});
		}
		if (
			this.keyDetectionReportFilters.clientIdn &&
			this.keyDetectionReportFilters.clientIdn.length > 0
		) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.keyDetectionReportFilters.clientIdn.map(
					(it) => it.idn || ''
				),
			});
		}

		return queryParameters;
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
